import { useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Button, Alert, MenuItem } from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { templesService } from '../../services/temples.service';

const feastSchema = z.object({
  name: z.string().min(1, 'Укажите название'),
  date: z.string().optional(),
  description: z.string().optional(),
  templeId: z.string().optional(),
});
type FeastFormValues = z.infer<typeof feastSchema>;

interface Props { open: boolean; onClose: () => void; onSubmit: (d: any) => Promise<void>; initialData?: any | null; loading: boolean; error?: string | null; }

export function FeastsForm({ open, onClose, onSubmit, initialData, loading, error }: Props) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FeastFormValues>({
    resolver: zodResolver(feastSchema),
    defaultValues: { name: '', date: '', description: '', templeId: '' },
  });
  const { data: templesData } = useQuery({ queryKey: ['temples', 'for-feast-select'], queryFn: () => templesService.getList({ limit: 100 }), enabled: open });
  const temples = templesData?.data || [];

  useEffect(() => {
    if (initialData) reset({ name: initialData.name || '', date: initialData.date || '', description: initialData.description || '', templeId: initialData.templeId ? String(initialData.templeId) : '' });
    else reset({ name: '', date: '', description: '', templeId: '' });
  }, [initialData, reset, open]);

  const submit = async (data: FeastFormValues) => {
    await onSubmit({ name: data.name, date: data.date || undefined, description: data.description || undefined, templeId: data.templeId ? parseInt(data.templeId) : null });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initialData ? 'Редактировать праздник' : 'Добавить праздник'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Название праздника" fullWidth {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
          <TextField label="Дата (напр. «7 января»)" fullWidth {...register('date')} />
          <TextField label="Описание" fullWidth multiline minRows={2} {...register('description')} />
          <TextField label="Храм (необязательно)" select fullWidth defaultValue="" {...register('templeId')}>
            <MenuItem value="">— не выбран —</MenuItem>
            {temples.map((t) => (<MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(submit)} variant="contained" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</Button>
      </DialogActions>
    </Dialog>
  );
}
